import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { classNames } from "src/common_util";

interface MenuPosition {
  top: number;
  right: number;
}

const DropdownButton = ({
  button,
  buttonClasses,
  menuContent,
}: {
  button: React.JSX.Element;
  menuContent: React.JSX.Element;
  buttonClasses: Record<string, boolean>;
}) : React.JSX.Element => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) { return; }

    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom, right: window.innerWidth - rect.right });
  }, []);

  const toggleExpanded = useCallback(() => {
    if (!isExpanded) {
      updateMenuPosition();
    }
    setIsExpanded(!isExpanded);
  }, [isExpanded, updateMenuPosition]);

  const baseButtonClasses = classNames(Object.assign({
    "button": true,
    "is-active": isExpanded,
  }, buttonClasses));

  const globalClickHandler = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) { return; }

    setIsExpanded(false);
  }, []);

  useEffect(() => {
    if (!isExpanded) { return; }

    window.addEventListener("click", globalClickHandler);
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      window.removeEventListener("click", globalClickHandler);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [isExpanded, globalClickHandler, updateMenuPosition]);

  // Portaled to document.body so the menu can escape ancestors with `overflow: hidden`
  // or scrollable containers (e.g. the dispatch dashboard's driver card list) that would
  // otherwise clip it. The portal keeps the same `.dropdown.is-right` nesting Bulma expects
  // so its CSS custom properties (background, shadow, right-alignment) still apply.
  return (
    <div className="dropdown is-right">
      <div ref={triggerRef} className="dropdown-trigger" onClick={toggleExpanded}>
        <button className={baseButtonClasses} aria-haspopup="true" aria-controls="dropdown-menu">
          {button}
        </button>
      </div>

      {isExpanded && menuPosition ? createPortal(
        <div
          ref={menuRef}
          className={classNames({ "dropdown": true, "is-right": true, "is-active": true })}
          style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }}
        >
          <div className="dropdown-menu" role="menu">
            <div className="dropdown-content" onClick={toggleExpanded}>
              {menuContent}
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

export { DropdownButton }
